import React, { useState, useRef, Fragment, createElement } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Patient, Encounter } from '../../types';
import { Search, FileText, Printer, Trash2, Download, Eye, ZoomIn, ZoomOut, X } from 'lucide-react';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
/**
 * Capture a DOM element and export it as a properly paginated A4 PDF.
 *
 * A4 layout: 210mm × 297mm, margins 20mm top/bottom, 18mm left/right.
 * Content slices are taken from the source canvas one page-height at a time
 * so rows never get clipped mid-content across page breaks.
 */
async function captureToA4Pdf(element: HTMLElement, filename: string) {
  // A4 geometry (millimetres)
  const PAGE_W_MM = 210;
  const PAGE_H_MM = 297;
  const MARGIN_TOP_MM = 20;
  const MARGIN_BOTTOM_MM = 20;
  const MARGIN_LEFT_MM = 18;
  const MARGIN_RIGHT_MM = 18;
  const CONTENT_W_MM = PAGE_W_MM - MARGIN_LEFT_MM - MARGIN_RIGHT_MM;
  const CONTENT_H_MM = PAGE_H_MM - MARGIN_TOP_MM - MARGIN_BOTTOM_MM;
  const sourceCanvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    allowTaint: true,
    backgroundColor: '#ffffff',
    logging: false
  });
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'portrait',
    compress: true
  });
  // How many source pixels equal one printed content page (in canvas pixels)?
  const sourceW = sourceCanvas.width;
  const sourceH = sourceCanvas.height;
  const pxPerMm = sourceW / CONTENT_W_MM;
  const pageSlicePx = Math.floor(CONTENT_H_MM * pxPerMm);
  // Slice the source canvas page-by-page; render each slice into its own
  // temporary canvas, paint that into the PDF. This avoids the "shift the
  // whole image up" trick that clips rows mid-cell.
  let cursorPx = 0;
  let pageIndex = 0;
  while (cursorPx < sourceH) {
    const remainingPx = sourceH - cursorPx;
    const sliceHpx = Math.min(pageSlicePx, remainingPx);
    const sliceCanvas = document.createElement('canvas');
    sliceCanvas.width = sourceW;
    sliceCanvas.height = sliceHpx;
    const ctx = sliceCanvas.getContext('2d');
    if (!ctx) break;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, sourceW, sliceHpx);
    ctx.drawImage(sourceCanvas, 0, cursorPx, sourceW, sliceHpx, 0, 0, sourceW, sliceHpx);
    const sliceImg = sliceCanvas.toDataURL('image/jpeg', 0.95);
    const sliceHmm = sliceHpx / pxPerMm;
    if (pageIndex > 0) pdf.addPage();
    pdf.addImage(sliceImg, 'JPEG', MARGIN_LEFT_MM, MARGIN_TOP_MM, CONTENT_W_MM, sliceHmm);
    cursorPx += sliceHpx;
    pageIndex += 1;
  }
  pdf.save(filename);
}
import { fbcStructure, urinalysisTests, testosteroneTests, hormonalProfileTests, amhStructure, hcgStructure, isHCGTest, isHCGAbnormal, isFBCTest, isUrinalysisTest, isTestosteroneTest, isHormonalProfileTest, isAMHTest } from '../../store/labStructures';
type ViewingTest = {
  kind: 'lab';
  encounterId: string;
  labId: string;
} | {
  kind: 'rad';
  encounterId: string;
  radId: string;
} | null;
export const PatientFiles: React.FC = () => {
  const {
    patients,
    encounters,
    deletePatient,
    currentUser
  } = useData();
  const navigate = useNavigate();
  // Amendment modal — Clinicians can reopen a completed encounter, provide a
  // required reason, and the Clinician Dashboard picks up that context on
  // arrival to log audit events on every save.
  const [amendModal, setAmendModal] = useState<{
    isOpen: boolean;
    encounterId: string;
    reason: string;
  }>({
    isOpen: false,
    encounterId: '',
    reason: ''
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  const [isGeneratingPdf, setIsGeneratingPdf] = useState(false);
  const [viewingTest, setViewingTest] = useState<ViewingTest>(null);
  const [imageZoom, setImageZoom] = useState<number>(100); // percentage 10-200
  const [isGeneratingTestPdf, setIsGeneratingTestPdf] = useState(false);
  const printableRef = useRef<HTMLDivElement>(null);
  const testModalRef = useRef<HTMLDivElement>(null);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const getPatientEncounters = (patientId: string) => {
    return encounters.filter(e => e.patientId === patientId).sort((a, b) => new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime());
  };
  const handleDeletePatient = (id: string) => {
    if (window.confirm('WARNING: This will permanently delete the patient and ALL associated medical records. Are you sure?')) {
      deletePatient(id);
      setSelectedPatient(null);
    }
  };
  const handlePrint = () => {
    window.print();
  };
  const handleDownloadPdf = async () => {
    if (!printableRef.current || !selectedPatient) return;
    setIsGeneratingPdf(true);
    try {
      const filename = `BMED_PatientFile_${selectedPatient.bmcFileNumber}_${selectedPatient.firstName}_${selectedPatient.lastName}.pdf`;
      await captureToA4Pdf(printableRef.current, filename);
    } catch (err) {
      console.error('PDF generation failed', err);
      alert('Failed to generate PDF. Please try again or use the Print option.');
    } finally {
      setIsGeneratingPdf(false);
    }
  };
  // ─── Test File modal helpers ───
  const closeTestModal = () => {
    setViewingTest(null);
    setImageZoom(100);
  };
  const handlePrintTestModal = () => {
    // Use a print-only class on body so only the modal prints
    document.body.classList.add('printing-test-modal');
    window.print();
    setTimeout(() => {
      document.body.classList.remove('printing-test-modal');
    }, 500);
  };
  const handleDownloadTestPdf = async () => {
    if (!testModalRef.current) return;
    setIsGeneratingTestPdf(true);
    try {
      const filename = `BMED_TestResult_${selectedPatient?.bmcFileNumber || 'patient'}_${Date.now()}.pdf`;
      await captureToA4Pdf(testModalRef.current, filename);
    } catch (err) {
      console.error('Test PDF generation failed', err);
      alert('Failed to generate PDF.');
    } finally {
      setIsGeneratingTestPdf(false);
    }
  };
  // ─── PATIENT FILE VIEW ───
  if (selectedPatient) {
    const patientEncounters = getPatientEncounters(selectedPatient.id);
    return <div className="space-y-6" data-id="element-301" data-mp="xtze9y9q8m9l">
        {/* Action Bar */}
        <div className="flex justify-between items-center print:hidden" data-id="element-302" data-mp="1bs62snssd9wl">
          <h1 className="text-2xl font-bold text-gray-900" data-id="element-303" data-mp="1bq33te9no6fq">Patient File</h1>
          <div className="flex space-x-2" data-id="element-304" data-mp="17soyhxp3iit0">
            <Button variant="outline" onClick={() => setSelectedPatient(null)} data-id="element-305" data-mp="1qfec1j2jb55x">
              Back to Directory
            </Button>
            <Button variant="outline" onClick={handlePrint} data-id="element-306" data-mp="1vcxz98h7msa0">
              <Printer className="w-4 h-4 mr-2" data-id="element-307" data-mp="1heq67bnaob44" /> Print
            </Button>
            <Button variant="primary" onClick={handleDownloadPdf} disabled={isGeneratingPdf} data-id="element-308" data-mp="1hgsv1scpny79">
              <Download className="w-4 h-4 mr-2" data-id="element-309" data-mp="17nlt6hh6j0bx" />
              {isGeneratingPdf ? 'Generating PDF...' : 'Download PDF'}
            </Button>
            {currentUser?.role === 'Administrator' && <Button variant="danger" onClick={() => handleDeletePatient(selectedPatient.id)} data-id="element-310" data-mp="yp02fz1k0f02">
                <Trash2 className="w-4 h-4 mr-2" data-id="element-311" data-mp="12nhcaqcd03l6" /> Delete File
              </Button>}
          </div>
        </div>

        {/* Encounter Status Summary (screen only) */}
        <div className="print:hidden bg-white rounded-lg shadow-sm p-4 border border-gray-200" data-id="element-312" data-mp="to3yxml46uv6">
          <h3 className="text-sm font-semibold text-gray-700 mb-2" data-id="element-313" data-mp="196dfeokz2vsr">
            Encounter History ({patientEncounters.length})
          </h3>
          <div className="flex flex-wrap gap-2" data-id="element-314" data-mp="105ypfbx2csix">
            {patientEncounters.map((enc, idx) => <span key={enc.id} className={`px-3 py-1 rounded-full text-xs font-medium ${enc.stage === 'COMPLETED' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`} data-id="element-315" data-mp="v7dy61zshj6m" data-mpi={idx}>
                Visit {patientEncounters.length - idx}:{' '}
                {new Date(enc.startedAt).toLocaleDateString()} •{' '}
                {enc.stage === 'COMPLETED' ? '✓ Completed' : enc.stage}
              </span>)}
          </div>
        </div>

        {/* All Encounters – Always Expanded — wrapped in ref for PDF capture */}
        <div ref={printableRef} data-id="element-316" data-mp="7je1klfsqyk4">
          {patientEncounters.length > 0 ? patientEncounters.map((enc, index) => <div key={enc.id} className={`bg-white border-2 border-gray-300 rounded-lg p-6 mb-6 ${index > 0 ? 'print:break-before-page pdf-page-break' : ''}`} data-id="element-317" data-mp="2e6lcpo0crjrm" data-mpi={index}>
                {/* Clinician-only Amend action for completed visits */}
                {currentUser?.role === 'Clinician' && enc.stage === 'COMPLETED' && <div className="print:hidden flex justify-end mb-3" data-id="element-318" data-mp="1vfu5ot3u85ge">
                      <Button variant="outline" className="text-amber-700 border-amber-400 hover:bg-amber-50" onClick={() => setAmendModal({
              isOpen: true,
              encounterId: enc.id,
              reason: ''
            })} data-id="element-319" data-mp="1vdhia0fb92vy">
                        <FileText className="w-4 h-4 mr-2" data-id="element-320" data-mp="1gp3ffphw9efe" />
                        Amend File
                      </Button>
                    </div>}
                {/* ═══ FORM HEADER ═══ */}
                <div className="flex justify-between items-start mb-6" data-id="element-321" data-mp="2auzhhkdi90sw">
                  <div className="flex items-center" data-id="element-322" data-mp="2exw73v1gpx6c">
                    <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED Medical Centre" className="h-20 w-20 object-contain mr-4" data-id="element-323" data-mp="2ewbc64b8kc7y" />
                    <div data-id="element-324" data-mp="1r9cvp6n494a1">
                      <h1 className="text-2xl font-bold text-pink-600 uppercase tracking-wider" data-id="element-325" data-mp="1r6h21uiax1re">
                        BMED MEDICAL CENTRE
                      </h1>
                      <p className="text-sm italic text-gray-600" data-id="element-326" data-mp="20z4uye65ulku">
                        Caring Healing Leading
                      </p>
                    </div>
                  </div>
                  <div className="text-right" data-id="element-327" data-mp="18h0n425vd6rg">
                    <p className="font-bold text-lg uppercase tracking-widest border-b-2 border-black pb-1 mb-2" data-id="element-328" data-mp="18fpk44k9031e">
                      Confidential
                    </p>
                    <p className="text-sm" data-id="element-329" data-mp="vzzojmo3s01w">
                      <span className="font-semibold" data-id="element-330" data-mp="r03i7grxnwj6">Date:</span>{' '}
                      {new Date(enc.startedAt).toLocaleDateString()}
                    </p>
                    <p className="text-sm" data-id="element-331" data-mp="myi5bvfefi8z">
                      <span className="font-semibold" data-id="element-332" data-mp="i0705y01tvx3">File No:</span>{' '}
                      {selectedPatient.bmcFileNumber}
                    </p>
                  </div>
                </div>

                {/* ═══ PERSONAL DETAILS ═══ */}
                <div className="border-2 border-black p-4 mb-6 text-sm space-y-3" data-id="element-333" data-mp="8a59qzk841f0">
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-334" data-mp="3c3pibb29w1y">
                    <div className="col-span-1 font-semibold" data-id="element-335" data-mp="3vdjsz77ipa3g">1.</div>
                    <div className="col-span-6" data-id="element-336" data-mp="3qfqku8mnm60j">
                      <span className="font-semibold" data-id="element-337" data-mp="3qg0cs1r9en8v">Full Names:</span>{' '}
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </div>
                    <div className="col-span-3" data-id="element-338" data-mp="3kuj642gr8ip6">
                      <span className="font-semibold" data-id="element-339" data-mp="36tos94axtpj4">Sex:</span>{' '}
                      {selectedPatient.sex}
                    </div>
                    <div className="col-span-2" data-id="element-340" data-mp="3btuokxs5k07j">
                      <span className="font-semibold" data-id="element-341" data-mp="3bumlqd1ee5zx">Age:</span>{' '}
                      {selectedPatient.age}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-342" data-mp="8aro569mlkbw">
                    <div className="col-span-1 font-semibold" data-id="element-343" data-mp="3prjc6ddltlga">2.</div>
                    <div className="col-span-11" data-id="element-344" data-mp="3ure1wdln84zr">
                      <span className="font-semibold" data-id="element-345" data-mp="3uq2tu1l60b2u">Marital Status:</span>{' '}
                      {selectedPatient.maritalStatus}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-346" data-mp="r3cdweu139bh">
                    <div className="col-span-1 font-semibold" data-id="element-347" data-mp="2brxzk4nf5eos">3.</div>
                    <div className="col-span-7" data-id="element-348" data-mp="26u4rf62k2alv">
                      <span className="font-semibold" data-id="element-349" data-mp="26td7isibm1bf">Home Address:</span>{' '}
                      {selectedPatient.address}
                    </div>
                    <div className="col-span-4" data-id="element-350" data-mp="2gj686ygxcw0w">
                      <span className="font-semibold" data-id="element-351" data-mp="2gkhnnvjqfs7u">Contact:</span>{' '}
                      {selectedPatient.contact}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-352" data-mp="1nw2brhvwdq0q">
                    <div className="col-span-1 font-semibold" data-id="element-353" data-mp="21v1y2ivxtstq">4.</div>
                    <div className="col-span-11" data-id="element-354" data-mp="1wvp3fbytrhgl">
                      <span className="font-semibold" data-id="element-355" data-mp="1wuxj4xaoh65m">Occupation:</span>{' '}
                      {selectedPatient.occupation || '_________________________'}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-356" data-mp="38cyn9zlv8bif">
                    <div className="col-span-1 font-semibold" data-id="element-357" data-mp="7hr3z1djhorv">5.</div>
                    <div className="col-span-5" data-id="element-358" data-mp="cfitjoqpoysw">
                      <span className="font-semibold" data-id="element-359" data-mp="hdu3s0kx5bbn">Next of Kin:</span>{' '}
                      {selectedPatient.nextOfKin}
                    </div>
                    <div className="col-span-3" data-id="element-360" data-mp="mdouo44p20wp">
                      <span className="font-semibold" data-id="element-361" data-mp="qeim37hchxuc">Contact:</span>{' '}
                      {selectedPatient.nokContact}
                    </div>
                    <div className="col-span-3" data-id="element-362" data-mp="vcajgh0rec5x">
                      <span className="font-semibold" data-id="element-363" data-mp="104llpqiz2a1n">Relationship:</span>{' '}
                      {selectedPatient.nokRelationship}
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2 border-b border-gray-300 pb-2" data-id="element-364" data-mp="15599827zs8r4">
                    <div className="col-span-1 font-semibold" data-id="element-365" data-mp="1a0ex36kyfxhu">6.</div>
                    <div className="col-span-11" data-id="element-366" data-mp="1e4wex07r80pz">
                      <span className="font-semibold" data-id="element-367" data-mp="2ym5gs6ln481i">Allergies:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-368" data-mp="2yk1718128etb">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>
                  <div className="grid grid-cols-12 gap-2" data-id="element-369" data-mp="2pnjtxz94y6ux">
                    <div className="col-span-1 font-semibold" data-id="element-370" data-mp="2kne7qylmxck8">7.</div>
                    <div className="col-span-11" data-id="element-371" data-mp="3i9v3hzepzi3v">
                      <span className="font-semibold" data-id="element-372" data-mp="3i9lf2g9bpj0a">HIV Status:</span>{' '}
                      <span className="ml-4" data-id="element-373" data-mp="3dc33ei3x8pol">
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Positive' ? 'bg-red-100 font-bold' : ''}`} data-id="element-374" data-mp="3dde58cdt3nd2">
                          Positive{' '}
                          {selectedPatient.hivStatus === 'Positive' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black mr-3 ${selectedPatient.hivStatus === 'Negative' ? 'bg-green-100 font-bold' : ''}`} data-id="element-375" data-mp="2xb4tq0f1w7">
                          Negative{' '}
                          {selectedPatient.hivStatus === 'Negative' ? '✓' : ''}
                        </span>
                        <span className={`inline-block px-2 py-0.5 border border-black ${selectedPatient.hivStatus === 'Unknown' ? 'bg-yellow-100 font-bold' : ''}`} data-id="element-376" data-mp="1pku8axz5qc7r">
                          Unknown{' '}
                          {selectedPatient.hivStatus === 'Unknown' ? '✓' : ''}
                        </span>
                      </span>
                    </div>
                  </div>
                </div>

                {/* ═══ NURSING OBSERVATIONS ═══ */}
                <div className="mb-6" data-id="element-377" data-mp="2w30rseh5xo7y">
                  <h3 className="font-bold uppercase bg-gray-200 p-2 border-2 border-black border-b-0 text-center" data-id="element-378" data-mp="2vzuzyrh5g88j">
                    Nursing Observations
                  </h3>
                  <table className="w-full border-collapse border-2 border-black text-sm text-center" data-id="element-379" data-mp="3b4zacgi2gobb">
                    <thead data-id="element-380" data-mp="363jmkgoi83p8">
                      <tr className="bg-gray-50" data-id="element-381" data-mp="3jwssg7d8u730">
                        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <th key={n} className="border border-black p-1 text-xs text-gray-500" data-id="element-382" data-mp="3f62pf0saigbh">
                            {n}
                          </th>)}
                      </tr>
                      <tr className="bg-gray-100" data-id="element-383" data-mp="6qsq20fsbp7e">
                        <th className="border border-black p-1" data-id="element-384" data-mp="6oesnkxmu0wo">Temp</th>
                        <th className="border border-black p-1" data-id="element-385" data-mp="2pfhvjp7z9xb">Pulse</th>
                        <th className="border border-black p-1" data-id="element-386" data-mp="28h45vh3wja2q">Resp</th>
                        <th className="border border-black p-1" data-id="element-387" data-mp="24gk7693ojyeh">BP</th>
                        <th className="border border-black p-1" data-id="element-388" data-mp="24idgjw5ogwq6">SpO2</th>
                        <th className="border border-black p-1" data-id="element-389" data-mp="2t0fpyfop0o3r">Weight (Kg)</th>
                        <th className="border border-black p-1" data-id="element-390" data-mp="2xziix1ref3vd">Height</th>
                        <th className="border border-black p-1" data-id="element-391" data-mp="32racll5upppq">BMI</th>
                        <th className="border border-black p-1" data-id="element-392" data-mp="32t4wcw5dzbs8">Pain Score</th>
                        <th className="border border-black p-1" data-id="element-393" data-mp="36s42gk2utel9">AVPU</th>
                      </tr>
                    </thead>
                    <tbody data-id="element-394" data-mp="2a4pkgihjx9l4">
                      <tr data-id="element-395" data-mp="2f0xu5gczcakw">
                        <td className="border border-black p-2 font-medium" data-id="element-396" data-mp="2f28xxg6fdic0">
                          {enc.vitals?.temp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-397" data-mp="2o1w1o8mmn088">
                          {enc.vitals?.pulse || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-398" data-mp="3uhonr9mhcnxp">
                          {enc.vitals?.resp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-399" data-mp="dgd9ytmz4a9y">
                          {enc.vitals?.bp || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-400" data-mp="8itxnz9u67kn">
                          {enc.vitals?.sp02 || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-401" data-mp="3vsry81kplayj">
                          {enc.vitals?.weight || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-402" data-mp="3lvn9btzhb1e1">
                          {enc.vitals?.height || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-403" data-mp="3gx2jmohus03u">
                          {enc.vitals?.bmi || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-404" data-mp="1f4xn0zpesems">
                          {enc.vitals?.painScore || ''}
                        </td>
                        <td className="border border-black p-2 font-medium" data-id="element-405" data-mp="1b1hpm15ima91">
                          {enc.vitals?.avpu || ''}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>

                {/* ═══ CLINICAL NOTES ═══ */}
                <div className="space-y-5 mb-6 text-sm" data-id="element-406" data-mp="350d4x42f38t5">
                  <div className="border-2 border-black" data-id="element-407" data-mp="302bpct479rmf">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-408" data-mp="2v4a6a86rxsay">
                      Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-409" data-mp="2q3nkyx551b17">
                      {enc.clinicalNote?.presentingComplaint || ''}
                    </div>
                  </div>

                  <div className="border-2 border-black" data-id="element-410" data-mp="2h63e98oiamj7">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-411" data-mp="kcs6g8gsuehm">
                      History of Presenting Complaint
                    </h3>
                    <div className="p-3 min-h-[80px] whitespace-pre-wrap" data-id="element-412" data-mp="fbb386auefxv">
                      {enc.clinicalNote?.historyOfPresentingComplaint || ''}
                    </div>
                  </div>

                  {/* PAST MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-413" data-mp="wnj7u09cp3fi">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-414" data-mp="11lkwd0pjpuyd">
                      Past Medical History / General Co-Morbidities
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-415" data-mp="8wvrcqldd5hc">
                      <tbody data-id="element-416" data-mp="8yzx7e2twek9">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA', 'RVD'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-417" data-mp="3qdd2ft2wg2cz">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-418" data-mp="3le0iph7brbem">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-419" data-mp="3ug1ee9mwtpe6">
                              {enc.clinicalNote?.pastMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* DRUG HISTORY */}
                  <div className="border-2 border-black" data-id="element-420" data-mp="2nxursk9lxx9r">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-421" data-mp="2izt6dsopllv4">
                      Drug History
                    </h3>
                    <div className="p-3 min-h-[40px]" data-id="element-422" data-mp="yg7m6815l260">
                      {enc.clinicalNote?.drugHistory || ''}
                    </div>
                    <div className="p-3 border-t border-gray-300" data-id="element-423" data-mp="ph2p7658pgvy">
                      <span className="font-semibold" data-id="element-424" data-mp="pixailkfbb0k">Any known allergy:</span>{' '}
                      <span className="text-red-600 font-medium" data-id="element-425" data-mp="ugytz7lrhcck">
                        {selectedPatient.allergies}
                      </span>
                    </div>
                  </div>

                  {/* FAMILY MEDICAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-426" data-mp="187kf72yotydm">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-427" data-mp="18728fceeg1rg">
                      Family Medical History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-428" data-mp="3swrmvob6xljm">
                      <tbody data-id="element-429" data-mp="1r8dwt0kk0dt">
                        {['DIABETES', 'EPILEPSY', 'TUBERCULOSIS', 'HYPERTENSION', 'ASTHMA', 'SICKLE CELL DISEASE', 'ANEMIA'].map(condition => <tr key={condition} className="border-b border-gray-300" data-id="element-430" data-mp="39qhxypk6szi">
                            <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-431" data-mp="zz7w722lxnct">
                              {condition}
                            </td>
                            <td className="p-2 text-center" data-id="element-432" data-mp="qyjdvv66l0u4">
                              {enc.clinicalNote?.familyMedicalHistory?.includes(condition) ? '✓ Yes' : ''}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* OBSTETRICS AND GYNAECOLOGY HISTORY */}
                  <div className="border-2 border-black" data-id="element-433" data-mp="31m4svmflr5tf">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-434" data-mp="36k6cq9kur95y">
                      Obstetrics and Gynaecology History
                    </h3>
                    <div className="p-3 min-h-[60px] whitespace-pre-wrap" data-id="element-435" data-mp="3fd1kb9xslgyw">
                      {enc.clinicalNote?.obgynHistory || ''}
                    </div>
                  </div>

                  {/* SOCIAL HISTORY */}
                  <div className="border-2 border-black" data-id="element-436" data-mp="3paxu9yezu5vf">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-437" data-mp="3p9d0wc4f0sz5">
                      Social History
                    </h3>
                    <table className="w-full border-collapse text-sm" data-id="element-438" data-mp="1uyr6m04wxwm8">
                      <tbody data-id="element-439" data-mp="1zvho8rwd909r">
                        <tr className="border-b border-gray-300" data-id="element-440" data-mp="1zwszfcs55aaw">
                          <td className="border-r border-gray-300 p-2 font-medium w-48" data-id="element-441" data-mp="1rus5mmrp4gfz">
                            Alcohol
                          </td>
                          <td className="p-2" data-id="element-442" data-mp="1myuyn72bomfv">
                            {enc.clinicalNote?.socialHistory?.alcohol || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-443" data-mp="1wtd5um6y704f">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-444" data-mp="2ate742r4k9kf">
                            Smoking
                          </td>
                          <td className="p-2" data-id="element-445" data-mp="25tiawpoo5mff">
                            {enc.clinicalNote?.socialHistory?.smoking || ''}
                          </td>
                        </tr>
                        <tr className="border-b border-gray-300" data-id="element-446" data-mp="2fn8ylscz1su2">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-447" data-mp="2tlyskz4hvce7">
                            Occupation
                          </td>
                          <td className="p-2" data-id="element-448" data-mp="387mh0whqd32v">
                            {enc.clinicalNote?.socialHistory?.occupation || ''}
                          </td>
                        </tr>
                        <tr data-id="element-449" data-mp="3d4cwbhlpnun8">
                          <td className="border-r border-gray-300 p-2 font-medium" data-id="element-450" data-mp="2z7h9mmcje4op">
                            Mental Status
                          </td>
                          <td className="p-2" data-id="element-451" data-mp="3447rnf7wjacr">
                            {enc.clinicalNote?.socialHistory?.mentalStatus || ''}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  {/* REVIEW OF SYSTEMS */}
                  <div className="border-2 border-black" data-id="element-452" data-mp="2khjran1ag97f">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-453" data-mp="2kfyxx0qpmwb5">
                      Review of Systems
                    </h3>
                    <div className="p-3 space-y-1" data-id="element-454" data-mp="2bg24kltdl99r">
                      <p data-id="element-455" data-mp="2qzekwl8jamze">
                        <span className="font-semibold" data-id="element-456" data-mp="2qzmx0awu1o9j">R/S:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.rs || ''}
                      </p>
                      <p data-id="element-457" data-mp="2hyonjd3mkojk">
                        <span className="font-semibold" data-id="element-458" data-mp="2hxx6d7arytct">CVS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cvs || ''}
                      </p>
                      <p data-id="element-459" data-mp="280inz25ajuhv">
                        <span className="font-semibold" data-id="element-460" data-mp="280sgawdt6dqq">CNS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.cns || ''}
                      </p>
                      <p data-id="element-461" data-mp="237zeag9q5jic">
                        <span className="font-semibold" data-id="element-462" data-mp="1ya7l7ixcfrck">GIT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.git || ''}
                      </p>
                      <p data-id="element-463" data-mp="1u7axjdsy551r">
                        <span className="font-semibold" data-id="element-464" data-mp="1pakgan5eo3er">GUT:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.gut || ''}
                      </p>
                      <p data-id="element-465" data-mp="1kdak8onigthy">
                        <span className="font-semibold" data-id="element-466" data-mp="3vyt7y9pqy3bd">MSS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.mss || ''}
                      </p>
                      <p data-id="element-467" data-mp="4ttgiu0v54j2">
                        <span className="font-semibold" data-id="element-468" data-mp="9rcwpzh4frdn">IS:</span>{' '}
                        {enc.clinicalNote?.reviewOfSystems?.is || ''}
                      </p>
                    </div>
                  </div>

                  {/* ON EXAMINATION */}
                  <div className="border-2 border-black" data-id="element-469" data-mp="3d9mvh4fjq7y0">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-470" data-mp="3i63mq7i1l2fb">
                      On Examination
                    </h3>
                    <div className="p-3 space-y-2 text-sm" data-id="element-471" data-mp="3m6nhj4f57tya">
                      {([{
                  key: 'gc',
                  label: 'G/C'
                }, {
                  key: 'febrile',
                  label: 'Temperature'
                }, {
                  key: 'pjc',
                  label: 'P | J | C'
                }, {
                  key: 's1s2',
                  label: 'S1  S2'
                }, {
                  key: 'rs',
                  label: 'R/S'
                }, {
                  key: 'pa',
                  label: 'P/A'
                }] as const).map(field => <div key={field.key} className="grid grid-cols-[6rem_1fr] gap-2" data-id="element-472" data-mp="1lg45x0kjgi7h">
                          <span className="font-bold" data-id="element-473" data-mp="1bipwvm0y2vp7">{field.label}:</span>
                          <span className="border-b border-gray-300 pb-0.5" data-id="element-474" data-mp="1bku001r0rqof">
                            {enc.clinicalNote?.onExaminationDetails?.[field.key] || '\u00A0'}
                          </span>
                        </div>)}
                      {enc.clinicalNote?.onExamination && <div className="pt-2 mt-2 border-t border-gray-200" data-id="element-475" data-mp="wufv18e4zvae">
                          <p className="text-xs font-bold uppercase text-gray-600 mb-1" data-id="element-476" data-mp="1auqlwbfg59vk">
                            Additional Notes
                          </p>
                          <p className="whitespace-pre-wrap" data-id="element-477" data-mp="1klljgby57vwb">
                            {enc.clinicalNote.onExamination}
                          </p>
                        </div>}
                    </div>
                  </div>

                  {/* SUMMARY OF SIGNIFICANT FINDINGS */}
                  <div className="border-2 border-black" data-id="element-478" data-mp="1pky3yq1jkqvq">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-479" data-mp="1pmiywgrrqbu4">
                      Summary of Significant Findings
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-480" data-mp="21nr523ratfka">
                      {enc.clinicalNote?.significantFindings?.filter(f => f.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-481" data-mp="2bdkbkqcl34ho">
                          {enc.clinicalNote.significantFindings.filter(f => f.trim()).map((finding, i) => <li key={i} data-id="element-482" data-mp="2pck3q7zb21il" data-mpi={i}>{finding}</li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-483" data-mp="2ualkund67qrf">
                          {[1, 2, 3, 4, 5, 6, 7, 8].map(n => <p key={n} data-id="element-484" data-mp="v0dei59ktk7a">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* WORKING DIAGNOSIS */}
                  <div className="border-2 border-black" data-id="element-485" data-mp="iokrjf2duqdf">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black" data-id="element-486" data-mp="dpzshiyvacqk">
                      Working Diagnosis
                    </h3>
                    <div className="p-3 min-h-[60px]" data-id="element-487" data-mp="1bambvtuqxyhq">
                      {enc.clinicalNote?.workingDiagnosis?.filter(d => d.trim()).length ? <ol className="list-decimal pl-5 space-y-1" data-id="element-488" data-mp="11gm0xbcs9z13">
                          {enc.clinicalNote.workingDiagnosis.filter(d => d.trim()).map((dx, i) => <li key={i} className="font-bold" data-id="element-489" data-mp="3h5nnkzdiuxxz" data-mpi={i}>
                                {dx}
                              </li>)}
                        </ol> : <div className="space-y-2 text-gray-400" data-id="element-490" data-mp="1em8et7f0vdx4">
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(n => <p key={n} data-id="element-491" data-mp="zupskrvkkn5b">
                              {n}.
                              ______________________________________________________________________________________
                            </p>)}
                        </div>}
                    </div>
                  </div>

                  {/* INVESTIGATIONS TO BE DONE */}
                  <div className="border-2 border-black" data-id="element-492" data-mp="1nm4z6wudpqmd">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-493" data-mp="1nkk2p1oi7xlv">
                      Investigations To Be Done
                    </h3>
                    <div className="p-4" data-id="element-494" data-mp="c814tpjvq0gs">
                      <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-495" data-mp="c4vgicj2r2m4">
                        Laboratory Tests
                      </h4>
                      <table className="w-full border-collapse text-sm mb-4" data-id="element-496" data-mp="2leuwyh1ybbc1">
                        <tbody data-id="element-497" data-mp="2zfp1uppsiu5y">
                          {[['FBC', 'GRAVINDEX'], ['MPS/RDT', 'HVS'], ['CREATININE / U&E', 'URINALYSIS'], ['LIVER FUNCTION TEST', 'PAP SMEAR'], ['AAFB', 'CD4 COUNT'], ['URINE MICROSCOPY', 'URETHRAL SWAB'], ['VBG', 'VIRAL LOAD'], ['HEPATITIS PROFILE', 'RBS / FBS'], ['ABG', 'STOOL MICROSCOPY'], ['HORMONAL PROFILE', 'ANTI-MÜLLERIAN HORMONE'], ['RPR', 'HIV'], ['SPERM ANALYSIS', 'TESTOSTERONE'], ['DOA 6', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-498" data-mp="3j1kmpa5vsp20" data-mpi={idx}>
                              {pair.map((test, ci) => {
                        if (!test) {
                          return <Fragment key={`empty-${ci}`} data-id="element-499" data-mp="1ngpkg4b71is">
                                      <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-500" data-mp="1ortcfxr899w"></td>
                                      <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-501" data-mp="306xcry70c15m"></td>
                                    </Fragment>;
                        }
                        const isOrdered = enc.labRequests.some(lab => lab.tests.some(t => t.toLowerCase().includes(test.toLowerCase()) || test.toLowerCase().includes(t.toLowerCase())));
                        return <Fragment key={test} data-id="element-502" data-mp="205910y2b6myl" data-mpi={ci}>
                                    <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-503" data-mp="202mtgyff47gd">
                                      {test}
                                    </td>
                                    <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-504" data-mp="1r2raw8jj9a6b">
                                      {isOrdered ? <span className="text-green-700 font-bold" data-id="element-505" data-mp="1h6oap888rlm0">
                                          ✓
                                        </span> : ''}
                                    </td>
                                  </Fragment>;
                      })}
                            </tr>)}
                        </tbody>
                      </table>

                      <h4 className="font-bold text-sm uppercase mb-2 border-b border-gray-400 pb-1" data-id="element-506" data-mp="2fntsbdf3ahd9">
                        Radiology Scans
                      </h4>
                      <table className="w-full border-collapse text-sm mb-4" data-id="element-507" data-mp="21mr1yoh2akwj">
                        <tbody data-id="element-508" data-mp="26nwxsv60qoaa">
                          {[['PELVIC SCAN', 'ANOMALY SCAN'], ['OBS SCAN', 'CXR (CHEST X-RAY)'], ['HSG (HYSTEROSALPINGOGRAPHY)', '']].map((pair, idx) => <tr key={idx} className="border-b border-gray-200" data-id="element-509" data-mp="3m5733nekstso" data-mpi={idx}>
                              {pair.map((scan, ci) => {
                        if (!scan) {
                          return <Fragment key={`empty-r-${ci}`} data-id="element-510" data-mp="1fdkdr5win8uy">
                                      <td className="p-1.5 border-r border-gray-200 w-1/3" data-id="element-511" data-mp="1ahvbuvx0xfvh"></td>
                                      <td className="p-1.5 w-1/6 border-r border-gray-300" data-id="element-512" data-mp="1p6869oggqqnv"></td>
                                    </Fragment>;
                        }
                        const isOrdered = enc.radiologyRequests.some(r => {
                          const a = r.scanType.toLowerCase();
                          const b = scan.toLowerCase();
                          return a.includes(b) || b.includes(a);
                        });
                        return <Fragment key={scan} data-id="element-513" data-mp="8r6pfsdx7kaz" data-mpi={ci}>
                                    <td className="p-1.5 font-medium border-r border-gray-200 w-1/3" data-id="element-514" data-mp="8sri1cgoct67">
                                      {scan}
                                    </td>
                                    <td className="p-1.5 w-1/6 text-center border-r border-gray-300" data-id="element-515" data-mp="15r978gp9k9gb">
                                      {isOrdered ? <span className="text-green-700 font-bold" data-id="element-516" data-mp="1eqvy48kd9wv4">
                                          ✓
                                        </span> : ''}
                                    </td>
                                  </Fragment>;
                      })}
                            </tr>)}
                        </tbody>
                      </table>

                      <div className="border-t border-gray-300 pt-2" data-id="element-517" data-mp="2cca81fuzkq5p">
                        <span className="font-semibold" data-id="element-518" data-mp="27gl5r4rl0v5p">
                          Other investigations:
                        </span>{' '}
                        {enc.labRequests.map(l => l.otherTests).filter(Boolean).join(', ') || ''}
                      </div>

                      {/* Investigation Results — View File list */}
                      {(enc.labRequests.some(l => l.status === 'Completed') || enc.radiologyRequests.some(r => r.status === 'Completed')) && <div className="mt-4 pt-4 border-t-2 border-black" data-id="element-519" data-mp="1ycee8bp7ch5w">
                          <h4 className="font-bold mb-3 uppercase text-center" data-id="element-520" data-mp="1yabdawmih3me">
                            Investigation Results
                          </h4>
                          <table className="w-full border-collapse text-sm" data-id="element-521" data-mp="1of9pc43z27le">
                            <thead data-id="element-522" data-mp="1tdupk3j84r9u">
                              <tr className="bg-gray-100" data-id="element-523" data-mp="1tctcxumk7wq0">
                                <th className="border border-black p-1.5 text-left" data-id="element-524" data-mp="1a981o3stf2e9">
                                  Test / Scan
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-525" data-mp="15g547kkx38ss">
                                  Type
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-526" data-mp="vl4skljh7al8">
                                  Performed By
                                </th>
                                <th className="border border-black p-1.5 text-left" data-id="element-527" data-mp="1o7xz5kjjr435">
                                  Completed
                                </th>
                                <th className="border border-black p-1.5 text-center w-32 print:hidden" data-id="element-528" data-mp="1jb7j2x7qs8hq">
                                  Action
                                </th>
                              </tr>
                            </thead>
                            <tbody data-id="element-529" data-mp="2u39i9cnwuwf">
                              {enc.labRequests.filter(l => l.status === 'Completed').flatMap(lab => lab.tests.filter(testName => !(lab.removedTestNames || []).includes(testName)).map(testName => <tr key={`${lab.id}-${testName}`} data-id="element-530" data-mp="32697gmcad6fk">
                                        <td className="border border-black p-1.5 font-medium" data-id="element-531" data-mp="37870g8ahnflp">
                                          {testName}
                                        </td>
                                        <td className="border border-black p-1.5" data-id="element-532" data-mp="1ctxjnh3ykx3k">
                                          Laboratory
                                        </td>
                                        <td className="border border-black p-1.5" data-id="element-533" data-mp="388taxd5re27e">
                                          —
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-534" data-mp="33cm1hae5m8kp">
                                          {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                        </td>
                                        <td className="border border-black p-1.5 text-center print:hidden" data-id="element-535" data-mp="2yboordbwy6ql">
                                          <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            encounterId: enc.id,
                            labId: lab.id,
                            testName
                          })} data-id="element-536" data-mp="2tk6iqav19fyr">
                                            <Eye className="w-3.5 h-3.5 mr-1" data-id="element-537" data-mp="2ag35r7vuxf7p" />{' '}
                                            View File
                                          </Button>
                                        </td>
                                      </tr>))}
                              {enc.labRequests.filter(l => l.status === 'Completed' && l.otherTests).map(lab => <tr key={`${lab.id}-other`} data-id="element-538" data-mp="2wmxfwodrn11i">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-539" data-mp="2rpe3zv4jj0cq">
                                      {lab.otherTests}{' '}
                                      <span className="text-xs text-gray-500" data-id="element-540" data-mp="2mwkxb1pihhy0">
                                        (Other)
                                      </span>
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-541" data-mp="2cy53us30hwlc">
                                      Laboratory
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-542" data-mp="240co5mtdomxf">
                                      —
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-543" data-mp="27y7ss6hfqj4">
                                      {lab.completedAt ? new Date(lab.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center print:hidden" data-id="element-544" data-mp="eiphd796tls3">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'lab',
                            encounterId: enc.id,
                            labId: lab.id
                          })} data-id="element-545" data-mp="ji24lt3z0uv7">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-546" data-mp="1c66ekqtkru3p" />{' '}
                                        View File
                                      </Button>
                                    </td>
                                  </tr>)}
                              {enc.radiologyRequests.filter(r => r.status === 'Completed').map(rad => <tr key={rad.id} data-id="element-547" data-mp="227qrcs7fr8jk">
                                    <td className="border border-black p-1.5 font-medium" data-id="element-548" data-mp="2g071ecyr86tz">
                                      {rad.scanType}
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-549" data-mp="2pywcnmw5at19">
                                      Radiology
                                    </td>
                                    <td className="border border-black p-1.5" data-id="element-550" data-mp="2l0mautfx0u6o">
                                      {rad.radiographerName || '—'}
                                    </td>
                                    <td className="border border-black p-1.5 text-xs" data-id="element-551" data-mp="qnd0z0jgj6nw">
                                      {rad.completedAt ? new Date(rad.completedAt).toLocaleString() : ''}
                                    </td>
                                    <td className="border border-black p-1.5 text-center print:hidden" data-id="element-552" data-mp="2xgm29fjsdyjs">
                                      <Button size="sm" variant="outline" onClick={() => setViewingTest({
                            kind: 'rad',
                            encounterId: enc.id,
                            radId: rad.id
                          })} data-id="element-553" data-mp="2jhcmui3en8gv">
                                        <Eye className="w-3.5 h-3.5 mr-1" data-id="element-554" data-mp="1e24vszlyxf2h" />{' '}
                                        View File
                                      </Button>
                                    </td>
                                  </tr>)}
                            </tbody>
                          </table>
                        </div>}
                    </div>
                  </div>

                  {/* DRUG TREATMENT / PRESCRIPTION */}
                  <div className="border-2 border-black" data-id="element-555" data-mp="21ouxfzuen7mk">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-556" data-mp="25rhwakphtst9">
                      Drug Treatment / Prescription
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-557" data-mp="be8fe7umb50z">
                      <thead data-id="element-558" data-mp="bgcullxyvqgk">
                        <tr className="bg-gray-100" data-id="element-559" data-mp="gcjsqsk4fxny">
                          <th className="border border-black p-1.5 w-10" data-id="element-560" data-mp="gf5ycmngc33j">
                            No.
                          </th>
                          <th className="border border-black p-1.5 text-left" data-id="element-561" data-mp="42byrnjlgepu">
                            Drug Name
                          </th>
                          <th className="border border-black p-1.5" data-id="element-562" data-mp="3v9epkdvisizj">Dose</th>
                          <th className="border border-black p-1.5" data-id="element-563" data-mp="3qb3iuc0iuolj">Route</th>
                          <th className="border border-black p-1.5" data-id="element-564" data-mp="rsy3b4ue3gv3">
                            Frequency
                          </th>
                          <th className="border border-black p-1.5" data-id="element-565" data-mp="mtuzfnq5bgop">
                            No. of Days
                          </th>
                          <th className="border border-black p-1.5" data-id="element-566" data-mp="dsnb9yhcfw8c">
                            Signature
                          </th>
                        </tr>
                      </thead>
                      <tbody data-id="element-567" data-mp="2yiw0961gh24i">
                        {enc.prescriptions.length > 0 ? enc.prescriptions.map((rx, i) => <tr key={rx.id} data-id="element-568" data-mp="301coj0dfcylo" data-mpi={i}>
                                <td className="border border-black p-1.5" data-id="element-569" data-mp="34z4lw9wu9cx9">
                                  {i + 1}
                                </td>
                                <td className="border border-black p-1.5 text-left font-medium" data-id="element-570" data-mp="2q1c4fc7qeegf">
                                  {rx.drugName}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-571" data-mp="2h8ikh1hcaqw8">
                                  {rx.dose}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-572" data-mp="27bdz33vbizgh">
                                  {rx.route}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-573" data-mp="2caywvayr51rm">
                                  {rx.frequency}
                                </td>
                                <td className="border border-black p-1.5" data-id="element-574" data-mp="22cswwywia5pe">
                                  {rx.days}
                                </td>
                                <td className="border border-black p-1.5 italic text-xs" data-id="element-575" data-mp="1jq398kpcs2yr">
                                  {enc.lastAttendingClinician || ''}
                                </td>
                              </tr>) : [1, 2, 3, 4, 5, 6, 7].map(i => <tr key={i} data-id="element-576" data-mp="22muz5czehgbs">
                                <td className="border border-black p-3" data-id="element-577" data-mp="22ljxpjtfgknu">{i}</td>
                                <td className="border border-black p-3" data-id="element-578" data-mp="1xojqv6or7dvo"></td>
                                <td className="border border-black p-3" data-id="element-579" data-mp="2bf58yt6dvjsi"></td>
                                <td className="border border-black p-3" data-id="element-580" data-mp="2bh9bb6omwaqo"></td>
                                <td className="border border-black p-3" data-id="element-581" data-mp="27e39qccgv7qj"></td>
                                <td className="border border-black p-3" data-id="element-582" data-mp="2lee3bn55v0fe"></td>
                                <td className="border border-black p-3" data-id="element-583" data-mp="2gerq3dpc1k6l"></td>
                              </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* STAT DOSES ADMINISTERED BY NURSING */}
                  {enc.statDoses && enc.statDoses.length > 0 && <div className="border-2 border-black" data-id="element-584" data-mp="3997m24god61o">
                      <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-585" data-mp="3e80rrappzsra">
                        Stat Doses Administered (Nursing)
                      </h3>
                      <table className="w-full border-collapse text-sm" data-id="element-586" data-mp="2lik40qd1p01e">
                        <thead data-id="element-587" data-mp="2qi6g2wh50a8m">
                          <tr className="bg-gray-100" data-id="element-588" data-mp="2qhwzfzhzfffl">
                            <th className="border border-black p-1.5 w-10" data-id="element-589" data-mp="2qkj7e08sbwyc">
                              No.
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-590" data-mp="5sqs099ayzmo">
                              Drug Name
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-591" data-mp="asvceadc0ei6">
                              Dose
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-592" data-mp="1qb1cc56javpy">
                              Route
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-593" data-mp="1lgx4ljpg2jnt">
                              Time Given
                            </th>
                            <th className="border border-black p-1.5 text-left" data-id="element-594" data-mp="1cjmq7phbyeeo">
                              Nurse
                            </th>
                          </tr>
                        </thead>
                        <tbody data-id="element-595" data-mp="12l7460x5tvc1">
                          {enc.statDoses.map((dose, idx) => <tr key={dose.id} data-id="element-596" data-mp="xmm90fwrm1ug" data-mpi={idx}>
                              <td className="border border-black p-1.5 text-center" data-id="element-597" data-mp="2wygbpn65b6rn">
                                {idx + 1}
                              </td>
                              <td className="border border-black p-1.5 font-medium" data-id="element-598" data-mp="2rzvg611u9b9j">
                                {dose.drugName}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-599" data-mp="v169pqsonhay">
                                {dose.dose}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-600" data-mp="ga5mugr6vkva">
                                {dose.route}
                              </td>
                              <td className="border border-black p-1.5" data-id="element-601" data-mp="191pe8nxxw58d">
                                {dose.givenAt ? new Date(dose.givenAt).toLocaleString() : ''}
                              </td>
                              <td className="border border-black p-1.5 italic text-xs" data-id="element-602" data-mp="141b32x11kf64">
                                {dose.nurseName || ''}
                              </td>
                            </tr>)}
                        </tbody>
                      </table>
                    </div>}

                  {/* FOLLOW UP / REVIEW DATE */}
                  <div className="border-2 border-black" data-id="element-603" data-mp="3c9g8tywnr859">
                    <h3 className="font-bold uppercase bg-gray-200 p-2 border-b border-black text-center" data-id="element-604" data-mp="3c8y3mcs0pjl7">
                      Next Follow Up / Review Date (If Any)
                    </h3>
                    <table className="w-full border-collapse text-sm text-center" data-id="element-605" data-mp="3m7x3p6ypw6wl">
                      <thead data-id="element-606" data-mp="2yiukhuofq5zz">
                        <tr className="bg-gray-100" data-id="element-607" data-mp="2yfoxqm3a3g7f">
                          <th className="border border-black p-1 w-10" data-id="element-608" data-mp="2tjhqmpz5bynw">No.</th>
                          <th className="border border-black p-1" data-id="element-609" data-mp="387ckwassyb33">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-610" data-mp="389fjhj80tcjf">No.</th>
                          <th className="border border-black p-1" data-id="element-611" data-mp="33fl24mfw19ni">Date</th>
                          <th className="border border-black p-1 w-10" data-id="element-612" data-mp="2fq9zezez956q">No.</th>
                          <th className="border border-black p-1" data-id="element-613" data-mp="2asqlk0m6yufb">Date</th>
                        </tr>
                      </thead>
                      <tbody data-id="element-614" data-mp="1pagn2r71zxg6">
                        {[0, 1, 2, 3, 4].map(row => <tr key={row} data-id="element-615" data-mp="1u8rwx1xcm7ye">
                            <td className="border border-black p-1 font-medium" data-id="element-616" data-mp="1u9tku6zgqoxj">
                              {row + 1}
                            </td>
                            <td className="border border-black p-2" data-id="element-617" data-mp="239q6dzqk37od">
                              {row === 0 && enc.reviewDate ? new Date(enc.reviewDate).toLocaleDateString() : ''}
                            </td>
                            <td className="border border-black p-1 font-medium" data-id="element-618" data-mp="2hx1gryb04lmw">
                              {row + 6}
                            </td>
                            <td className="border border-black p-2" data-id="element-619" data-mp="2myqpv8enyp8c"></td>
                            <td className="border border-black p-1 font-medium" data-id="element-620" data-mp="2qzkc7xcgjvz4">
                              {row + 11}
                            </td>
                            <td className="border border-black p-2" data-id="element-621" data-mp="k9qvh0okn5c1"></td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>

                  {/* NAME OF MEDICAL PRACTITIONER */}
                  <div className="border-2 border-black p-4 mt-6" data-id="element-622" data-mp="b9khldoyo3cc">
                    <p className="font-bold text-sm" data-id="element-623" data-mp="6b9ahaq1w6xt">
                      Name of the Medical Practitioner:{' '}
                      <span className="font-normal border-b border-black inline-block min-w-[250px] ml-2" data-id="element-624" data-mp="12zyub6l8n9mf">
                        {enc.lastAttendingClinician || ''}
                      </span>
                    </p>
                  </div>
                </div>
              </div>) : <Card data-id="element-625" data-mp="p8tplq30jr58">
              <p className="text-gray-500 text-center py-8" data-id="element-626" data-mp="p793mourlgiz">
                No encounters found for this patient.
              </p>
            </Card>}
        </div>

        {/* Amendment Reason Modal */}
        {amendModal.isOpen && <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" data-id="element-627" data-mp="1zm0wgc6ybaoz">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6" data-id="element-628" data-mp="1ul3jce0st6uc">
              <h3 className="text-lg font-bold text-gray-900 mb-2" data-id="element-629" data-mp="1pm0eatktj0md">
                Amend Completed Visit
              </h3>
              <p className="text-sm text-gray-600 mb-4" data-id="element-630" data-mp="1kp9wa0pgduyb">
                Please provide a reason for amending this completed file. Your
                name, the timestamp, and this reason will be recorded in the
                audit trail.
              </p>
              <textarea className="w-full border border-gray-300 rounded-md p-2 text-sm focus:ring-2 focus:ring-amber-500 focus:outline-none" rows={4} value={amendModal.reason} onChange={e => setAmendModal({
            ...amendModal,
            reason: e.target.value
          })} placeholder="e.g., Correction of working diagnosis after MDT discussion" data-id="element-631" data-mp="28l5uziiij415" />
              <div className="flex justify-end space-x-3 mt-4" data-id="element-632" data-mp="38mj3mzd8v97z">
                <Button variant="outline" onClick={() => setAmendModal({
              isOpen: false,
              encounterId: '',
              reason: ''
            })} data-id="element-633" data-mp="3dkkmpkao78jg">
                  Cancel
                </Button>
                <Button variant="primary" className="bg-amber-600 hover:bg-amber-700 text-white" disabled={!amendModal.reason.trim()} onClick={() => {
              navigate('/dashboard', {
                state: {
                  amendEncounterId: amendModal.encounterId,
                  amendReason: amendModal.reason.trim()
                }
              });
            }} data-id="element-634" data-mp="3rj11zzmlm7d7">
                  Open in Consultation
                </Button>
              </div>
            </div>
          </div>}

        {viewingTest && (() => {
        const enc = encounters.find(e => e.id === viewingTest.encounterId);
        if (!enc) return null;
        const lab = viewingTest.kind === 'lab' ? enc.labRequests.find(l => l.id === viewingTest.labId) : null;
        const rad = viewingTest.kind === 'rad' ? enc.radiologyRequests.find(r => r.id === viewingTest.radId) : null;
        if (!lab && !rad) return null;
        return <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4 print:p-0 print:bg-white" data-id="element-635" data-mp="d5p25voby81k">
                <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[95vh] overflow-y-auto print:max-h-none print:max-w-none print:shadow-none print:rounded-none print:overflow-visible test-modal-print" data-id="element-636" data-mp="2jwokau64lw6f">
                  <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-3 flex justify-between items-center z-10 print:hidden" data-id="element-637" data-mp="zcjffl1jc9zl">
                    <h3 className="text-lg font-semibold text-gray-900" data-id="element-638" data-mp="kndhe0efopta">
                      {lab ? 'Laboratory Result' : 'Radiology Result'}
                    </h3>
                    <div className="flex items-center space-x-2" data-id="element-639" data-mp="bmyu4vhwt0n5">
                      <Button variant="outline" size="sm" onClick={handlePrintTestModal} data-id="element-640" data-mp="fotunxs0n9zv">
                        <Printer className="w-4 h-4 mr-1" data-id="element-641" data-mp="1r57gjah7qrn" /> Print
                      </Button>
                      <Button variant="primary" size="sm" onClick={handleDownloadTestPdf} disabled={isGeneratingTestPdf} data-id="element-642" data-mp="6q86nn3rz2rp">
                        <Download className="w-4 h-4 mr-1" data-id="element-643" data-mp="10f596x3yqgs" />
                        {isGeneratingTestPdf ? 'Generating...' : 'Download PDF'}
                      </Button>
                      <button onClick={closeTestModal} className="p-1.5 hover:bg-gray-100 rounded" aria-label="Close" data-id="element-644" data-mp="3o4lco4bo6ulh">
                        <X className="w-5 h-5 text-gray-500" data-id="element-645" data-mp="3e65mq4odprdk" />
                      </button>
                    </div>
                  </div>

                  <div ref={testModalRef} className="p-8 bg-white" data-id="element-646" data-mp="39e5pwl3lvel7">
                    <div className="flex justify-between items-start mb-6 border-b-2 border-black pb-4" data-id="element-647" data-mp="34fug2adb942z">
                      <div className="flex items-center" data-id="element-648" data-mp="30ftt21c5bkkb">
                        <img src="https://cdn.magicpatterns.com/uploads/3HmLapdTMRvTJbmSkdh1g8/Bmedhospital.png" alt="BMED" className="h-16 w-16 object-contain mr-3" data-id="element-649" data-mp="30d7ma3x2x935" />
                        <div data-id="element-650" data-mp="15yaklt6qd42l">
                          <h1 className="text-xl font-bold text-pink-600 uppercase" data-id="element-651" data-mp="1axwtxrjfu064">
                            BMED MEDICAL CENTRE
                          </h1>
                          <p className="text-xs italic text-gray-600" data-id="element-652" data-mp="1fxhsw1yly8iu">
                            Caring Healing Leading
                          </p>
                        </div>
                      </div>
                      <div className="text-right text-sm" data-id="element-653" data-mp="na5gulq2pj3s">
                        <p className="font-bold uppercase border-b border-black pb-1 mb-2" data-id="element-654" data-mp="s6m8hpwheflm">
                          {lab ? 'Laboratory Report' : 'Radiology Report'}
                        </p>
                        <p data-id="element-655" data-mp="10zz6h69ek7ek">
                          <span className="font-semibold" data-id="element-656" data-mp="111abf9el3l79">Patient:</span>{' '}
                          {selectedPatient.firstName} {selectedPatient.lastName}
                        </p>
                        <p data-id="element-657" data-mp="8id5ektegp7c">
                          <span className="font-semibold" data-id="element-658" data-mp="8hlfnqmhhloz">File No:</span>{' '}
                          {selectedPatient.bmcFileNumber}
                        </p>
                        <p data-id="element-659" data-mp="16u7m5tgv039d">
                          <span className="font-semibold" data-id="element-660" data-mp="16uz5o5x6maja">Age/Sex:</span>{' '}
                          {selectedPatient.age} / {selectedPatient.sex}
                        </p>
                        <p data-id="element-661" data-mp="1gmdb0ubte4kc">
                          <span className="font-semibold" data-id="element-662" data-mp="1gn58kaoz2cd9">Date:</span>{' '}
                          {new Date((lab?.completedAt || rad?.completedAt) ?? Date.now()).toLocaleString()}
                        </p>
                        {lab?.performedBy && <p data-id="element-663" data-mp="xu18a6h9119o">
                            <span className="font-semibold" data-id="element-664" data-mp="xszn396iqye8">Performed By:</span>{' '}
                            {lab.performedBy}
                          </p>}
                      </div>
                    </div>

                    {lab && (() => {
                const focusedTest = viewingTest?.kind === 'lab' ? viewingTest.testName : undefined;
                const showFBC = focusedTest && isFBCTest(focusedTest);
                const showUA = focusedTest && isUrinalysisTest(focusedTest);
                const showT = focusedTest && isTestosteroneTest(focusedTest);
                const showHP = focusedTest && isHormonalProfileTest(focusedTest);
                const showAMH = focusedTest && isAMHTest(focusedTest);
                const showHCG = focusedTest && isHCGTest(focusedTest);
                return <div data-id="element-665" data-mp="3ihsypv8ih05x">
                            <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-666" data-mp="3igrkjhw77xjz">
                              {focusedTest || 'Tests Performed'}
                            </h2>
                            {showFBC ? <div className="space-y-4" data-id="element-667" data-mp="1igl9x4wb30if">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-668" data-mp="1if1svc2tslfl">
                                  <h3 className="font-bold uppercase" data-id="element-669" data-mp="1ef9jwy9isd9p">
                                    Full Blood Count (FBC) Report
                                  </h3>
                                </div>
                                {fbcStructure.map(cat => <div key={cat.category} className="border border-black" data-id="element-670" data-mp="225dlz17284yl">
                                    <div className="bg-gray-100 p-2 font-bold border-b border-black text-xs uppercase" data-id="element-671" data-mp="1s5377c5dmphb">
                                      {cat.category}
                                    </div>
                                    <table className="w-full border-collapse text-sm" data-id="element-672" data-mp="gpeiaf8kbgdv">
                                      <thead data-id="element-673" data-mp="6odkmxk1zofp">
                                        <tr className="bg-gray-50" data-id="element-674" data-mp="6l7xvoywcyn5">
                                          <th className="border border-black p-1.5 text-left" data-id="element-675" data-mp="bmnjpj8wf96l">
                                            Test
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-32" data-id="element-676" data-mp="ldi3mgyo9wov">
                                            Result
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-32" data-id="element-677" data-mp="ubc1hwc34ye5">
                                            Reference Range
                                          </th>
                                          <th className="border border-black p-1.5 text-left w-24" data-id="element-678" data-mp="14aayuireh7lu">
                                            Unit
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody data-id="element-679" data-mp="1gbkq6czwpuzq">
                                        {cat.tests.map(t => <tr key={t.name} data-id="element-680" data-mp="1l4nkwogf7ahi">
                                            <td className="border border-black p-1.5" data-id="element-681" data-mp="17c7o45e448p1">
                                              {t.name}
                                            </td>
                                            <td className="border border-black p-1.5 font-bold" data-id="element-682" data-mp="1zzttnj6whnej">
                                              {lab.results && lab.results[t.name] || '—'}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-683" data-mp="1uzz017vqqjps">
                                              {t.range}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-684" data-mp="jmwleid7c489">
                                              {t.unit}
                                            </td>
                                          </tr>)}
                                      </tbody>
                                    </table>
                                  </div>)}
                              </div> : showUA ? <div data-id="element-685" data-mp="3ohwp9zkwu3qv">
                                <div className="text-center border-b-2 border-black pb-2 mb-6" data-id="element-686" data-mp="3kixliiax0d10">
                                  <h3 className="font-bold uppercase" data-id="element-687" data-mp="24y4nqafe3h7">
                                    Urinalysis Report
                                  </h3>
                                </div>
                                <div className="max-w-md mx-auto space-y-2" data-id="element-688" data-mp="35szrvmu7uv4f">
                                  {urinalysisTests.map(t => {
                        const key = `UA_${t.name}`;
                        const value = lab.results && (lab.results[key] || lab.results[t.name]) || '';
                        return <div key={t.name} className="flex items-center text-sm" data-id="element-689" data-mp="1jev2y0kthsdc">
                                        <span className="w-16 font-bold" data-id="element-690" data-mp="qs0uy356slgr">
                                          {t.name}
                                        </span>
                                        <span className="mx-3 text-gray-400" data-id="element-691" data-mp="zsfht6xsu8md">
                                          -
                                        </span>
                                        <span className="flex-1 border-b border-gray-400 pb-0.5 font-medium" data-id="element-692" data-mp="2b7cdv0sehy38">
                                          {value || '\u00A0'}
                                        </span>
                                      </div>;
                      })}
                                </div>
                                <div className="max-w-md mx-auto mt-10 grid grid-cols-2 gap-4 text-sm" data-id="element-693" data-mp="3muyomud0xqbw">
                                  <div data-id="element-694" data-mp="3hvvui4yl14ip">
                                    <p className="font-semibold mb-2" data-id="element-695" data-mp="3czexsmdbhhu0">
                                      Dr.'s Name:
                                    </p>
                                    <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-696" data-mp="3825958xr5a78">
                                      {lab.results && lab.results['UA_DR_NAME'] || '\u00A0'}
                                    </p>
                                  </div>
                                  <div data-id="element-697" data-mp="x8snlsoom08r">
                                    <p className="font-semibold mb-2" data-id="element-698" data-mp="x5oau6x5hw6h">
                                      Signature:
                                    </p>
                                    <p className="border-b border-black pb-0.5 min-h-[1.25rem]" data-id="element-699" data-mp="3v09jt7prjjx3">
                                      {lab.results && lab.results['UA_DR_SIG'] || '\u00A0'}
                                    </p>
                                  </div>
                                </div>
                              </div> : showT ? <div className="space-y-2" data-id="element-700" data-mp="2ybbn3jygdbxa">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-701" data-mp="2ycwi1aooiwvo">
                                  <h3 className="font-bold uppercase" data-id="element-702" data-mp="32d6pysw17xd6">
                                    Testosterone Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-703" data-mp="2tb74nodzi74f">
                                  <thead data-id="element-704" data-mp="29qyl260csmy6">
                                    <tr className="bg-gray-100" data-id="element-705" data-mp="24r3rtvt3vl9y">
                                      <th className="border border-black p-1.5 text-left" data-id="element-706" data-mp="24snblwdz0egh">
                                        Test
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-707" data-mp="2eo8jgb5q08y5">
                                        Result
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-708" data-mp="2nqq2kgq2wlgx">
                                        Reference (Male)
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-709" data-mp="2xf81stipmkig">
                                        Reference (Female)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-710" data-mp="37eh0u0sfmpbo">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-711" data-mp="3je44nbzep5tf">
                                    {testosteroneTests.map(t => <tr key={t.name} data-id="element-712" data-mp="3t61jqttjchxf">
                                        <td className="border border-black p-1.5" data-id="element-713" data-mp="3t7l1kouub11b">
                                          {t.name}
                                        </td>
                                        <td className="border border-black p-1.5 font-bold" data-id="element-714" data-mp="60amgk3ltj17">
                                          {lab.results && lab.results[`TESTO_${t.name}`] || lab.results[t.name] || '—'}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-715" data-mp="2hq36sev8lbqx">
                                          {t.rangeMale}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-716" data-mp="u4i9au96ng9i">
                                          {t.rangeFemale}
                                        </td>
                                        <td className="border border-black p-1.5 text-xs" data-id="element-717" data-mp="l4vjz6tqa0wt">
                                          {t.unit}
                                        </td>
                                      </tr>)}
                                  </tbody>
                                </table>
                              </div> : showHP ? <div data-id="element-718" data-mp="13ts4wk38jh2d">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-719" data-mp="13wxqvqgki2tv">
                                  <h3 className="font-bold uppercase" data-id="element-720" data-mp="3ti2g2lg2l2e8">
                                    Hormonal Profile Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-721" data-mp="1skabu47bjg5g">
                                  <thead data-id="element-722" data-mp="1xibvaq8nphhg">
                                    <tr className="bg-gray-100" data-id="element-723" data-mp="1ik9p08a8qhwi">
                                      <th className="border border-black p-1.5 text-left" data-id="element-724" data-mp="1ilumo6ruqgyl">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-725" data-mp="2b973s1f8twki">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-726" data-mp="21i2rj9gycptd">
                                        Reference Range
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-727" data-mp="2u6hvn0yjpnpx">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-728" data-mp="ikfy6mgtcvtk">
                                    {hormonalProfileTests.map(h => <Fragment key={h.key} data-id="element-729" data-mp="x93u4tukn7k1">
                                        <tr className="align-top" data-id="element-730" data-mp="s9r4608elkd8">
                                          <td className="border border-black p-1.5 font-medium" data-id="element-731" data-mp="saj1bfhnfq5m">
                                            {h.name}
                                          </td>
                                          <td className="border border-black p-1.5 font-bold" data-id="element-732" data-mp="3vs0zswbj7ktd">
                                            {lab.results && (lab.results[`HP_${h.key}`] || lab.results[h.name] || lab.results[h.key]) || '—'}
                                          </td>
                                          <td className="border border-black p-1.5 text-xs" data-id="element-733" data-mp="tw4vc7gm466f">
                                            {h.ranges.map(r => <div key={r.label} data-id="element-734" data-mp="12xw1fdf2ef0t">
                                                <span className="font-medium" data-id="element-735" data-mp="12x2xslbek5mw">
                                                  {r.label}:
                                                </span>{' '}
                                                {r.value}
                                              </div>)}
                                          </td>
                                          <td className="border border-black p-1.5 text-xs" data-id="element-736" data-mp="k0bbe30kdaem">
                                            {h.unit}
                                          </td>
                                        </tr>
                                        <tr data-id="element-737" data-mp="3ip9zswuyws2b">
                                          <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-738" data-mp="3ioh3kptmxkyf">
                                            Method: {h.method}
                                          </td>
                                        </tr>
                                      </Fragment>)}
                                  </tbody>
                                </table>
                              </div> : showAMH ? <div data-id="element-739" data-mp="27xkeuql8i4j0">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-740" data-mp="27w9fd2ytnixp">
                                  <h3 className="font-bold uppercase" data-id="element-741" data-mp="2cuaujct4my3w">
                                    Anti Mullerian Hormone (AMH) Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-742" data-mp="11em76kbynx2k">
                                  <thead data-id="element-743" data-mp="hdsz6xil4a64">
                                    <tr className="bg-gray-100" data-id="element-744" data-mp="dd95y514tqp9">
                                      <th className="border border-black p-1.5 text-left" data-id="element-745" data-mp="df3ppg0o3crr">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-746" data-mp="3mxodfoizizw">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-747" data-mp="3psuki2n8dpxf">
                                        Reference Range (by age, ng/mL)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-748" data-mp="3gvakex13tzoe">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-749" data-mp="1mmnfj8w26aez">
                                    <tr className="align-top" data-id="element-750" data-mp="1mllvwg0z8fln">
                                      <td className="border border-black p-1.5 font-medium" data-id="element-751" data-mp="1rjx30izw0c06">
                                        {amhStructure.name}
                                      </td>
                                      <td className="border border-black p-1.5 font-bold" data-id="element-752" data-mp="219qdths7gn3d">
                                        {lab.results && (lab.results[amhStructure.storageKey] || focusedTest && lab.results[focusedTest] || lab.results['AMH'] || lab.results['Anti-Müllerian Hormone'] || lab.results['ANTI-MÜLLERIAN HORMONE'] || lab.results['Anti Mullerian Hormone (AMH)']) || '—'}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-753" data-mp="3aha4tdzc1qs6">
                                        {amhStructure.ageRanges.map(a => <div key={a.ageBand} data-id="element-754" data-mp="3k8wolypqky6j">
                                            <span className="font-medium" data-id="element-755" data-mp="3f9tkcghkyvzm">
                                              {a.ageBand}:
                                            </span>{' '}
                                            {a.range}
                                          </div>)}
                                      </td>
                                      <td className="border border-black p-1.5 text-xs" data-id="element-756" data-mp="gqjq9uw9j6z3">
                                        {amhStructure.unit}
                                      </td>
                                    </tr>
                                    <tr data-id="element-757" data-mp="7p2cwka8pje3">
                                      <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-758" data-mp="7o0svqb8xmk8">
                                        Method: {amhStructure.method}
                                      </td>
                                    </tr>
                                  </tbody>
                                </table>
                                <div className="mt-3 border border-amber-300 bg-amber-50 rounded p-3 text-xs" data-id="element-759" data-mp="367zi6lnafumw">
                                  <p className="font-bold uppercase mb-1" data-id="element-760" data-mp="3b5smf9516ekj">
                                    Interpretation
                                  </p>
                                  <ul className="list-disc pl-5 space-y-0.5 text-gray-700" data-id="element-761" data-mp="1byklzyvogtt0">
                                    {amhStructure.interpretation.map((line, i) => <li key={i} data-id="element-762" data-mp="130ied0hfv2wr" data-mpi={i}>{line}</li>)}
                                  </ul>
                                </div>
                              </div> : showHCG ? <div data-id="element-763" data-mp="1qn8chqqo2dc3">
                                <div className="text-center border-b-2 border-black pb-2 mb-3" data-id="element-764" data-mp="1qqcsdldhuxil">
                                  <h3 className="font-bold uppercase" data-id="element-765" data-mp="1lrt1r7nygxjw">
                                    Beta HCG (Quantitative) Report
                                  </h3>
                                </div>
                                <table className="w-full border-collapse text-sm" data-id="element-766" data-mp="6cudkvyd5n4">
                                  <thead data-id="element-767" data-mp="4i9u7olffjk">
                                    <tr className="bg-gray-100" data-id="element-768" data-mp="54mw6ec6n4hp">
                                      <th className="border border-black p-1.5 text-left" data-id="element-769" data-mp="a4izscgywtwq">
                                        Test Description
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-32" data-id="element-770" data-mp="ex2drajkhr22">
                                        Value
                                      </th>
                                      <th className="border border-black p-1.5 text-left" data-id="element-771" data-mp="ny0ijv1ty2lp">
                                        Reference Range (mIU/mL)
                                      </th>
                                      <th className="border border-black p-1.5 text-left w-20" data-id="element-772" data-mp="xs0twenpg42v">
                                        Unit
                                      </th>
                                    </tr>
                                  </thead>
                                  <tbody data-id="element-773" data-mp="15ckyhjblc27f">
                                    {(() => {
                          const hcgValue = lab.results && (lab.results[hcgStructure.storageKey] || focusedTest && lab.results[focusedTest] || lab.results['HCG'] || lab.results['Beta HCG']) || '';
                          const abnormal = isHCGAbnormal(hcgValue);
                          return <>
                                          <tr className="align-top" data-id="element-774" data-mp="22ta5rsjb7wo7">
                                            <td className="border border-black p-1.5 font-medium" data-id="element-775" data-mp="22v33ujfux8kl">
                                              {hcgStructure.name}
                                            </td>
                                            <td className={`border border-black p-1.5 font-bold ${abnormal ? 'text-red-600 bg-red-50' : ''}`} data-id="element-776" data-mp="2f99lvihqdem0">
                                              {hcgValue || '—'}
                                              {abnormal && <div className="text-xs font-normal mt-0.5" data-id="element-777" data-mp="2y1kgyq65889k">
                                                  Above normal threshold (&gt; 6
                                                  mIU/mL)
                                                </div>}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-778" data-mp="3gtn14bhzu6ok">
                                              {hcgStructure.ranges.map(r => <div key={r.label} data-id="element-779" data-mp="rlt1odxf6arz">
                                                  <span className="font-medium" data-id="element-780" data-mp="rjyf6v6i2als">
                                                    {r.label}:
                                                  </span>{' '}
                                                  {r.range}
                                                </div>)}
                                            </td>
                                            <td className="border border-black p-1.5 text-xs" data-id="element-781" data-mp="dmjpdpcc40ta">
                                              {hcgStructure.unit}
                                            </td>
                                          </tr>
                                          <tr data-id="element-782" data-mp="1p16wlyxmnp61">
                                            <td colSpan={4} className="border border-black p-1.5 text-xs italic text-gray-600 bg-gray-50" data-id="element-783" data-mp="1u0jl0o45d4aq">
                                              Method: {hcgStructure.method}
                                            </td>
                                          </tr>
                                        </>;
                        })()}
                                  </tbody>
                                </table>
                              </div> : <>
                                <p className="mb-4 text-sm" data-id="element-784" data-mp="2luoqgdx0a5m3">
                                  <span className="font-semibold" data-id="element-785" data-mp="2gvlpp5o26ljx">
                                    Tests Ordered:
                                  </span>{' '}
                                  {lab.tests.filter(t => !(lab.removedTestNames || []).includes(t)).join(', ')}
                                  {lab.otherTests ? `, ${lab.otherTests}` : ''}
                                </p>
                                {lab.results && Object.keys(lab.results).length > 0 ? <table className="w-full border-collapse text-sm" data-id="element-786" data-mp="2xa1ki67hoa44">
                                    <thead data-id="element-787" data-mp="2ic93taq7hfoc">
                                      <tr className="bg-gray-100" data-id="element-788" data-mp="2ibfxubz2mu79">
                                        <th className="border border-black p-2 text-left" data-id="element-789" data-mp="2nast9l40d9lg">
                                          Parameter
                                        </th>
                                        <th className="border border-black p-2 text-left" data-id="element-790" data-mp="1bvx61arapzta">
                                          Result
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody data-id="element-791" data-mp="3l6xvzfbjimyt">
                                      {Object.entries(lab.results).map(([k, v]) => <tr key={k} data-id="element-792" data-mp="3v4c7r1miqnks">
                                            <td className="border border-black p-2 font-medium" data-id="element-793" data-mp="37d6tus0525d4">
                                              {k}
                                            </td>
                                            <td className="border border-black p-2" data-id="element-794" data-mp="3g5ia5l1jgcfd">
                                              {v}
                                            </td>
                                          </tr>)}
                                    </tbody>
                                  </table> : <p className="text-gray-500 italic" data-id="element-795" data-mp="2we4qbema5zfa">
                                    No structured results recorded for this
                                    test.
                                  </p>}

                                {lab.labComments && <div className="mt-6 border border-gray-300 bg-gray-50 rounded p-4" data-id="element-796" data-mp="3b3kdylqiloqr">
                                    <h3 className="font-bold uppercase mb-2 text-sm" data-id="element-797" data-mp="3g0jble9e0dtp">
                                      Comments / Observations / Interpretation
                                    </h3>
                                    <pre className="whitespace-pre-wrap font-sans text-sm text-gray-800" data-id="element-798" data-mp="3p2k1tr67dzlv">
                                      {lab.labComments}
                                    </pre>
                                  </div>}
                              </>}
                          </div>;
              })()}

                    {rad && <div data-id="element-799" data-mp="1nc1qj7d8gs12">
                        <h2 className="text-lg font-bold mb-3 uppercase" data-id="element-800" data-mp="1nbtfzm4l1yt1">
                          {rad.scanType}
                        </h2>
                        {rad.radiographerName && <p className="text-sm mb-3" data-id="element-801" data-mp="1dfqbi9m9do2x">
                            <span className="font-semibold" data-id="element-802" data-mp="2b1w3doxjyglb">
                              Sonographer / Radiographer:
                            </span>{' '}
                            {rad.radiographerName}
                          </p>}
                        {rad.report && <div className="mb-4 border border-black p-4" data-id="element-803" data-mp="1w559twak7z3m">
                            <pre className="whitespace-pre-wrap font-sans text-sm" data-id="element-804" data-mp="1w79dcd4jqw3d">
                              {rad.report}
                            </pre>
                          </div>}
                        {rad.images && rad.images.length > 0 && <div data-id="element-805" data-mp="38tkmkuowgwoi">
                            <div className="flex justify-between items-center mb-3 print:hidden" data-id="element-806" data-mp="3cx0fph1rgewg">
                              <h3 className="font-bold" data-id="element-807" data-mp="2yxrbzguqrd9d">Scan Images</h3>
                              <div className="flex items-center space-x-2 bg-gray-100 px-3 py-1.5 rounded-lg" data-id="element-808" data-mp="2yzln63g7nr09">
                                <button onClick={() => setImageZoom(z => Math.max(10, z - 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom out" data-id="element-809" data-mp="2p7poora7pyvi">
                                  <ZoomOut className="w-4 h-4" data-id="element-810" data-mp="2l6ci2ezaprnj" />
                                </button>
                                <input type="range" min={10} max={200} step={10} value={imageZoom} onChange={e => setImageZoom(Number(e.target.value))} className="w-32" data-id="element-811" data-mp="269lpaok4ne6w" />
                                <button onClick={() => setImageZoom(z => Math.min(200, z + 10))} className="p-1 hover:bg-gray-200 rounded" aria-label="Zoom in" data-id="element-812" data-mp="1wnkkbidbbzzh">
                                  <ZoomIn className="w-4 h-4" data-id="element-813" data-mp="2fc923vgi1p71" />
                                </button>
                                <span className="text-sm font-medium w-12 text-right" data-id="element-814" data-mp="2u9hyx4oknl65">
                                  {imageZoom}%
                                </span>
                              </div>
                            </div>
                            <div className="space-y-4 print:space-y-2" data-id="element-815" data-mp="1ewdv023w2ql2">
                              {rad.images.map((img, idx) => <div key={idx} className="border-2 border-gray-300 rounded overflow-auto bg-gray-50 max-h-[600px] print:max-h-none print:overflow-visible" data-id="element-816" data-mp="1jws1vgtr7uhi" data-mpi={idx}>
                                  <div style={{
                        transform: `scale(${imageZoom / 100})`,
                        transformOrigin: 'top left',
                        display: 'inline-block'
                      }} data-id="element-817" data-mp="w5mor9f77gaw">
                                    <img src={img} alt={`${rad.scanType} ${idx + 1}`} className="block" data-id="element-818" data-mp="8er1mplot99z" />
                                  </div>
                                </div>)}
                            </div>
                          </div>}
                      </div>}
                  </div>
                </div>
              </div>;
      })()}
      </div>;
  }
  // ─── PATIENT DIRECTORY ───
  return <div className="space-y-6" data-id="element-819" data-mp="22zaqxd14fajo">
      <div className="flex justify-between items-center" data-id="element-820" data-mp="22z2etncto99j">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-821" data-mp="1yxz0x6ec4nay">Patient Directory</h1>
      </div>

      <Card data-id="element-822" data-mp="1u074q06nqf0y">
        <div className="relative" data-id="element-823" data-mp="1u0qfmwxw435h">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-824" data-mp="1txkolc5par74">
            <Search className="h-5 w-5 text-gray-400" data-id="element-825" data-mp="1ozt1cvg03t99" />
          </div>
          <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-826" data-mp="1k2sr08c4c4cc" />
        </div>
      </Card>

      <Card noPadding data-id="element-827" data-mp="2yoln022ndkcg">
        <div className="overflow-x-auto" data-id="element-828" data-mp="20zabhn1hui5q">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-829" data-mp="210lbrcvqd7s3">
            <thead className="bg-gray-50" data-id="element-830" data-mp="25xbrfz3mi1cz">
              <tr data-id="element-831" data-mp="25ydel1xwyeb2">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-832" data-mp="25zy8cpcelt7v">
                  BMC Number
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-833" data-mp="2f0cxjzshnsgn">
                  Patient Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-834" data-mp="3qdfc6pb127y6">
                  Age/Sex
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-835" data-mp="1espmngtoqfba">
                  Registered
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-836" data-mp="15x9qgt5gjo2j">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-837" data-mp="w16ktdbed7au">
              {filteredPatients.length > 0 ? filteredPatients.map(patient => <tr key={patient.id} className="hover:bg-gray-50" data-id="element-838" data-mp="mz5ocinzmpa8">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-blue-600" data-id="element-839" data-mp="n1jlqy654dky">
                      {patient.bmcFileNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-840" data-mp="2hc59erb50bow">
                      {patient.firstName} {patient.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-841" data-mp="2vhxis1am2y66">
                      {patient.age} / {patient.sex}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-842" data-mp="2miaen7qhze9f">
                      {new Date(patient.registeredAt).toLocaleDateString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-843" data-mp="2rf0ze8d8yy16">
                      <Button variant="outline" size="sm" onClick={() => setSelectedPatient(patient)} data-id="element-844" data-mp="3f551ucep8rql">
                        <FileText className="w-4 h-4 mr-2" data-id="element-845" data-mp="35b5y58yz8zwj" /> View File
                      </Button>
                    </td>
                  </tr>) : <tr data-id="element-846" data-mp="1tvpr6id57mb5">
                  <td colSpan={5} className="px-6 py-12 text-center text-gray-500" data-id="element-847" data-mp="1yspysxpn4x4d">
                    No patients found matching your search.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>;
};